import type {FC} from 'react';
import React from 'react';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	ListItem,
	Text,
	UnorderedList,
} from '@tempo/core';
import {useTranslation} from '@app/hooks/useTranslation';
import {logCenter} from '@app/modules/tracking/logCenter';
import {useAppState} from '@app/modules/apps/context';

type PermissionsList = {
	user: Permission[];
	read: Permission[];
	modify: Permission[];
};

type Permission = {
	permission: string;
	translation: string;
};

const PermissionsList: FC<{
	appId?: string;
	scopes: string[];
	spacing?: number;
}> = ({appId, scopes, spacing = 8}) => {
	const t = useTranslation();
	const {isOauth} = useAppState();

	const PERMISSIONS_MAP = [
		{
			type: 'account-data',
			scopes: ['private-info-user-read'],
		},
		{
			type: 'read',
			scopes: [
				'private-recommendation-all-read',
				'private-recommendation-artists-read',
				'private-recommendation-artists-playlists-read',
				'private-recommendation-new-releases-read',
				'private-recommendation-playlists-read',
				'private-madeforme-user-read',
				'playlist-read',
				'playlist-public-collaborative-read',
				'favorite-all-read',
				'favorite-playlist-read',
				'favorite-artist-read',
				'favorite-album-read',
				'favorite-track-read',
				'favorite-podcast-read',
				'recently-played-user-read',
				'flow-user-read',
				'following-user-read',
				'followers-user-read',
			],
		},
		{
			type: 'modify',
			scopes: [
				'partner-playlist-modify',
				'playlist-modify',
				'playlist-public-collaborative-modify',
				'favorite-all-modify',
				'favorite-playlist-modify',
				'favorite-artist-modify',
				'favorite-album-modify',
				'favorite-track-modify',
				'favorite-podcast-modify',
				'following-user-modify',
				'followers-user-modify',
			],
		},
	];
	const TRANSLATIONS_MAP = {
		'account-data': t('permissions.viewyouraccountdata'),
		read: t('permissions.viewyouractivityondeezer'),
		modify: t('permissions.modifyyouractivityondeezer'),
		'private-info-user-read': t('permissions.viewpictureandusername'),
		'private-recommendation-all-read': t(
			'permissions.viewmusicrecommendations',
		),
		'private-recommendation-artists-read': t(
			'permissions.viewmusicrecommendations',
		),
		'private-recommendation-artists-playlists-read': t(
			'permissions.viewplrecommendationslinkedtoartist',
		),
		'private-recommendation-new-releases-read': t(
			'permissions.viewnewreleaserecommendations',
		),
		'private-recommendation-playlists-read': t(
			'permissions.viewplaylistrecommendations',
		),
		'private-madeforme-user-read': t('permissions.viewmixesinspiredby'),
		'playlist-read': t('permissions.viewpersonalplaylists'),
		'playlist-public-collaborative-read': t(
			'permissions.viewpublicandcollaborativeplaylists',
		),
		'favorite-all-read': t('permissions.viewfavoritemusiccontent'),
		'favorite-playlist-read': t('permissions.viewfavoriteplaylists'),
		'favorite-artist-read': t('permissions.viewfavoriteartists'),
		'favorite-album-read': t('permissions.viewfavoritealbums'),
		'favorite-track-read': t('permissions.viewfavoritetracks'),
		'favorite-podcast-read': t('permissions.viewfavoritepodcasts'),
		'recently-played-user-read': t('permissions.viewrecentlyplayedcontent'),
		'flow-user-read': t('permissions.viewflow'),
		'following-user-read': t('permissions.accesswhoyoufollow'),
		'followers-user-read': t('permissions.accessfollowers'),
		'playlist-modify': t('permissions.modifyyourpersonalplaylists'),
		'partner-playlist-modify': t('permissions.modifyyourpersonalplaylists'),
		'playlist-public-collaborative-modify': t(
			'permissions.modifypublicandcollaborativeplaylists',
		),
		'favorite-all-modify': t('permissions.modifyfavoritemusiccontent'),
		'favorite-playlist-modify': t('permissions.modifyfavoriteplaylists'),
		'favorite-artist-modify': t('permissions.modifyfavoriteartists'),
		'favorite-album-modify': t('permissions.modifyfavoritealbums'),
		'favorite-track-modify': t('permissions.modifyfavoritetracks'),
		'favorite-podcast-modify': t('permissions.modifyfavoritepodcasts'),
		'following-user-modify': t('permissions.modifywhoyoufollow'),
		'followers-user-modify': t('permissions.modifyfollowers'),
	};

	const getLogAction = (type: (typeof PERMISSIONS_MAP)[0]['type']) => {
		switch (type) {
			case 'account-data':
				return 'view_deezer_data';
			case 'read':
				return 'view_deezer_activity';
			case 'modify':
				return 'modify_deezer_activity';
			default:
				return '';
		}
	};

	const hasScopeAll = scopes.includes('all');

	return (
		<>
			{PERMISSIONS_MAP.map((elem) => {
				if (hasScopeAll || elem.scopes.some((scope) => scopes.includes(scope)))
					return (
						<Accordion key={elem.type} allowMultiple w="100%">
							<AccordionItem pb={spacing}>
								<AccordionButton
									data-testid={`permissions-${elem.type}-expand${
										appId ? `-${appId}` : ''
									}`}
									_hover={{
										background: 'none',
									}}
									onClick={() => {
										isOauth &&
											logCenter.log({
												type: 'cdp',
												eventName: 'oauth_allow_client_data_sharing',
												customAttributes: {
													action: getLogAction(elem.type),
												},
											});
									}}
								>
									<Text fontWeight="bold" w="100%" textAlign="start">
										{
											TRANSLATIONS_MAP[
												elem.type as keyof typeof TRANSLATIONS_MAP
											]
										}
									</Text>
									<AccordionIcon />
								</AccordionButton>
								<AccordionPanel
									data-testid={`${elem.type}-expanded${
										appId ? `-${appId}` : ''
									}`}
								>
									<UnorderedList>
										{elem.scopes.map((perm) => {
											if (hasScopeAll || scopes.includes(perm))
												return (
													<ListItem key={perm}>
														{
															TRANSLATIONS_MAP[
																perm as keyof typeof TRANSLATIONS_MAP
															]
														}
													</ListItem>
												);
										})}
									</UnorderedList>
								</AccordionPanel>
							</AccordionItem>
						</Accordion>
					);
			})}
		</>
	);
};

export default PermissionsList;
